import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { graphql } from 'react-apollo';
import { findIndex } from 'lodash';
import { useOnboardingState } from '../../contexts/OnboardingContext';

// Using mutation with fields that exist in the schema
const UPDATE_CURRENT_USER = gql`
  mutation updateCurrentUser(
    $transition_goals: [String!]
    $locale: String
  ) {
    updateCurrentUser(
      transition_goals: $transition_goals
      locale: $locale
    )
  }
`;

// Create a higher-order component to connect the class component to context
const withOnboardingContext = WrappedComponent => {
  return props => {
    const context = useOnboardingState() || {};
    const currentLanguage = context.currentLanguage || props.currentLanguage || 'en';
    console.log('MotivationStep language from context:', currentLanguage);
    return <WrappedComponent {...props} currentLanguage={currentLanguage} onboardingContext={context} />;
  };
};

class MotivationStep extends React.Component {
  constructor(props) {
    super(props);
    
    // Get initial data from resumeData (GraphQL fields aren't available)
    const resumeData = window.__WIZARD_DATA__?.resumeData || {};
    
    // Initialize with transition goals from props.user or resumeData
    const userGoals = props.user?.transition_goals || [];
    
    this.state = {
      transition_goals: userGoals.length > 0 ? userGoals : (resumeData?.transition_goals || []),
      selectedCard: null,
      flippedCards: {},
      errors: {},
      isSubmitting: false
    };
    
    // Bind methods
    this.toggleGoal = this.toggleGoal.bind(this);
    this.validateForm = this.validateForm.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCardSelect = this.handleCardSelect.bind(this);
    this.isCardSelected = this.isCardSelected.bind(this);
    this.getTranslatedText = this.getTranslatedText.bind(this);
  }
  
  componentDidMount() {
    // Log business type information for debugging
    console.log('MotivationStep business type:', {
      fromUser: this.props.user?.company?.business_type,
      fromWizardData: window.__WIZARD_DATA__?.companySettings?.business_type,
      user: this.props.user
    });
    
    // Filter out any exploring options if business type is p_and_c
    this.filterExploringOptionsIfNeeded();
  }
  
  componentDidUpdate(prevProps) {
    // Check if user or transition_goals changed
    if (this.props.user && 
        (!prevProps.user || 
         JSON.stringify(prevProps.user?.transition_goals) !== JSON.stringify(this.props.user.transition_goals))) {
      
      const userGoals = this.props.user.transition_goals || [];
      
      // Only update if we have goals from the user and they're different from current state
      if (userGoals.length > 0 && JSON.stringify(userGoals) !== JSON.stringify(this.state.transition_goals)) {
        
        // Update state with user's transition goals
        this.setState({ 
          transition_goals: userGoals,
          // Pre-select cards that have selected options
          flippedCards: this.getFlippedCardsFromGoals(userGoals)
        }, () => {
          // Filter out exploring options if needed after setting state
          this.filterExploringOptionsIfNeeded();
        });
      }
    }
  }
  
  // Helper method to filter out "exploring" options if business type is p_and_c
  filterExploringOptionsIfNeeded() {
    const businessType = this.props.user?.company?.business_type || 
                         window.__WIZARD_DATA__?.companySettings?.business_type;
    
    // If business type is p_and_c, filter out any selected exploring options
    if (businessType === 'p_and_c') {
      // Get all options from the exploring card
      const exploringCard = this.motivationCards.find(card => card.id === 'exploring');
      if (!exploringCard) return;
      
      const exploringOptionIds = exploringCard.options.map(option => option.id);
      
      // Filter transition_goals to remove any exploring options
      const filteredGoals = this.state.transition_goals.filter(
        goalId => !exploringOptionIds.includes(goalId)
      );
      
      // Update state only if something changed
      if (filteredGoals.length !== this.state.transition_goals.length) {
        this.setState({ transition_goals: filteredGoals });
      }
    }
  }
  
  // Helper to determine which cards should be flipped based on selected goals
  getFlippedCardsFromGoals(goals) {
    if (!goals || goals.length === 0) return {};
    
    const flippedCards = {};
    
    // Check which cards have selected options
    this.motivationCards.forEach(card => {
      const hasSelectedOption = card.options.some(option => 
        goals.includes(option.id)
      );
      
      if (hasSelectedOption) {
        flippedCards[card.id] = true;
      }
    });
    
    return flippedCards;
  }
  
  // Cards configuration with main motivation types and their sub-options
  get motivationCards() {
    return [
      {
        id: 'succession',
        title: this.getTranslatedText('succession.title'),
        icon: (
          <div className="flex items-center justify-center w-16 h-16 mb-4 bg-succession-primary-10 rounded-full" style={{ backgroundColor: 'var(--succession-primary-10, rgba(84, 137, 182, 0.1))' }}>
            <svg width="32" height="32" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M24 12L12 24M24 12L36 24M24 12V36" stroke="var(--succession-primary, #5489B6)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        ),
        description: this.getTranslatedText('succession.description'),
        options: [
          { id: 'Selling my practice', label: this.getTranslatedText('option.selling.practice') },
          { id: 'Selling part of my practice', label: this.getTranslatedText('option.selling.part') },
          { id: 'Finding a Long-Term Successor', label: this.getTranslatedText('option.finding.successor') }
        ]
      },
      {
        id: 'growth',
        title: this.getTranslatedText('growth.title'),
        icon: (
          <div className="flex items-center justify-center w-16 h-16 mb-4 bg-succession-primary-10 rounded-full" style={{ backgroundColor: 'var(--succession-primary-10, rgba(84, 137, 182, 0.1))' }}>
            <svg width="32" height="32" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 36L36 12M36 12H20M36 12V28" stroke="var(--succession-primary, #5489B6)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        ),
        description: this.getTranslatedText('growth.description'),
        options: [
          { id: 'Buying', label: this.getTranslatedText('option.buying') },
          { id: 'Become a Successor', label: this.getTranslatedText('option.become.successor') },
          { id: 'Becoming a Junior Partner', label: this.getTranslatedText('option.junior.partner') },
          { id: 'Forming a Partnership', label: this.getTranslatedText('option.forming.partnership') },
          { id: 'Finding a Junior Partner', label: this.getTranslatedText('option.finding.junior') }
        ]
      },
      {
        id: 'exploring',
        title: this.getTranslatedText('exploring.title'),
        icon: (
          <div className="flex items-center justify-center w-16 h-16 mb-4 bg-succession-primary-10 rounded-full" style={{ backgroundColor: 'var(--succession-primary-10, rgba(84, 137, 182, 0.1))' }}>
            <svg width="32" height="32" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="24" cy="24" r="12" stroke="var(--succession-primary, #5489B6)" strokeWidth="3"/>
              <path d="M24 20V25" stroke="var(--succession-primary, #5489B6)" strokeWidth="3" strokeLinecap="round"/>
              <circle cx="24" cy="30" r="1.5" fill="var(--succession-primary, #5489B6)"/>
            </svg>
          </div>
        ),
        description: this.getTranslatedText('exploring.description'),
        options: [
          { id: 'Currently not interested in Buying / Selling', label: this.getTranslatedText('option.not.interested') }
        ]
      }
    ];
  }
  
  // Toggle a goal in the transition_goals array
  toggleGoal(goalId) {
    const { transition_goals } = this.state;
    
    // Check if goal already exists
    const index = findIndex(transition_goals, g => g === goalId);
    let updatedGoals = [...transition_goals];
    
    if (index > -1) {
      // Remove goal if it already exists
      updatedGoals.splice(index, 1);
    } else {
      // Add goal if it doesn't exist
      // First, determine which bucket this goal belongs to
      let bucketId = null;
      
      // Find which card/bucket contains this goal
      this.motivationCards.forEach(card => {
        if (card.options.some(option => option.id === goalId)) {
          bucketId = card.id;
        }
      });
      
      if (bucketId) {
        // Clear goals from other buckets
        updatedGoals = transition_goals.filter(existingGoalId => {
          // Check which bucket the existing goal belongs to
          let existingGoalBucketId = null;
          this.motivationCards.forEach(card => {
            if (card.options.some(option => option.id === existingGoalId)) {
              existingGoalBucketId = card.id;
            }
          });
          
          // Keep goals only from the same bucket
          return existingGoalBucketId === bucketId;
        });
        
        // Add the new goal
        updatedGoals.push(goalId);
      } else {
        // If bucket not found (shouldn't happen), just add the goal
        updatedGoals.push(goalId);
      }
    }
    
    this.setState({ 
      transition_goals: updatedGoals,
      errors: {}
    });
  }
  
  // Handle card selection
  handleCardSelect(cardId) {
    this.setState(prevState => ({
      selectedCard: prevState.selectedCard === cardId ? null : cardId,
      flippedCards: {
        ...prevState.flippedCards,
        [cardId]: !prevState.flippedCards[cardId]
      }
    }));
  }
  
  // Check if a card is selected
  isCardSelected(cardId) {
    const { motivationCards } = this;
    const { transition_goals } = this.state;
    
    const card = motivationCards.find(card => card.id === cardId);
    if (!card) return false;
    
    return card.options.some(option => 
      transition_goals.includes(option.id)
    );
  }
  
  validateForm() {
    const { transition_goals } = this.state;
    const newErrors = {};
    
    if (transition_goals.length === 0) {
      newErrors.transition_goals = this.getTranslatedText('at.least.one');
    }
    
    this.setState({ errors: newErrors });
    return Object.keys(newErrors).length === 0;
  }

  handleSubmit(e) {
    e.preventDefault();
    
    if (!this.validateForm()) return;
    
    this.setState({ isSubmitting: true });
    
    // For GraphQL, we'll use transition_goals which exists in the schema
    const variables = {
      transition_goals: this.state.transition_goals,
      locale: this.props.currentLanguage
    };
    
    // Update wizard data for other components
    if (window.__WIZARD_DATA__) {
      window.__WIZARD_DATA__.resumeData = {
        ...window.__WIZARD_DATA__.resumeData,
        transition_goals: this.state.transition_goals
      };
    }
    
    // Execute GraphQL mutation
    this.props.mutate({ variables })
      .then(response => {
        this.setState({ isSubmitting: false });
        
        // Update user data in parent component
        const userData = {
          transition_goals: this.state.transition_goals,
          id: this.props.user?.id
        };
        
        if (this.props.onUserUpdate) {
          this.props.onUserUpdate(userData);
        }
        
        // Move to next step
        if (this.props.onNext) this.props.onNext();
      })
      .catch(error => {
        console.error("Error saving transition goals:", error);
        this.setState({ 
          isSubmitting: false,
          errors: { 
            general: error.message ? error.message.replace('GraphQL error:', '') : 'An error occurred'
          }
        });
      });
  }

  // Get translated text based on current language
  getTranslatedText(key) {
    const { currentLanguage } = this.props;
    
    // Define translations
    const translations = {
      en: {
        'title': 'Choose Your Path',
        'subtitle': 'Select the option that best represents your goals for your practice.',
        'selection.rule.title': 'Important Selection Rule',
        'selection.rule': 'You can only select options from one category at a time. Selecting an option from a different category will clear your previous selections.',
        'business.type.message': "Based on your business type, we're showing the most relevant options for your needs.",
        'at.least.one': 'You must select at least one option to continue.',
        'selected': 'Selected',
        'click.to.change': 'Click to change category',
        'click.to.see.options': 'Click to see options',
        'back': 'Back',
        'continue': 'Continue',
        'saving': 'Saving...',
        'succession.title': 'Succession Planning',
        'succession.description': 'I want to prepare for transitioning my practice to a successor within the next 5-10 years.',
        'growth.title': 'Growth & Expansion',
        'growth.description': 'I want to grow my practice by adding clients, advisors, or acquiring other practices.',
        'exploring.title': 'Exploring Your Options',
        'exploring.description': "I'm not sure yet and would like to explore all the options available to me.",
        'option.selling.practice': 'Selling my practice',
        'option.selling.part': 'Selling part of my practice',
        'option.finding.successor': 'Finding a Long-Term Successor',
        'option.buying': 'Buying a book',
        'option.become.successor': 'Becoming a successor',
        'option.junior.partner': 'Becoming a junior partner',
        'option.forming.partnership': 'Forming a partnership',
        'option.finding.junior': 'Finding a junior partner',
        'option.not.interested': 'Currently not interested in Buying / Selling'
      },
      fr: {
        'title': 'Choisissez Votre Voie',
        'subtitle': 'Sélectionnez l\'option qui représente le mieux vos objectifs pour votre pratique.',
        'selection.rule.title': 'Règle de Sélection Importante',
        'selection.rule': 'Vous ne pouvez sélectionner des options que dans une seule catégorie à la fois. La sélection d\'une option dans une catégorie différente effacera vos sélections précédentes.',
        'business.type.message': 'En fonction de votre type d\'entreprise, nous vous montrons les options les plus pertinentes pour vos besoins.',
        'at.least.one': 'Vous devez sélectionner au moins une option pour continuer.',
        'selected': 'Sélectionné',
        'click.to.change': 'Cliquez pour changer de catégorie',
        'click.to.see.options': 'Cliquez pour voir les options',
        'back': 'Retour',
        'continue': 'Continuer',
        'saving': 'Enregistrement...',
        'succession.title': 'Planification de la Relève',
        'succession.description': 'Je veux préparer la transition de ma pratique vers un successeur au cours des 5 à 10 prochaines années.',
        'growth.title': 'Croissance et Expansion',
        'growth.description': 'Je veux développer ma pratique en ajoutant des clients, des conseillers ou en acquérant d\'autres pratiques.',
        'exploring.title': 'Explorer les Options',
        'exploring.description': 'Je ne suis pas encore sûr et j\'aimerais explorer toutes les options qui s\'offrent à moi.',
        'option.selling.practice': 'Vendre ma pratique',
        'option.selling.part': 'Vendre une partie de ma pratique',
        'option.finding.successor': 'Trouver un successeur à long terme',
        'option.buying': 'Acheter un livre',
        'option.become.successor': 'Devenir un successeur',
        'option.junior.partner': 'Devenir un associé junior',
        'option.forming.partnership': 'Former un partenariat',
        'option.finding.junior': 'Trouver un associé junior',
        'option.not.interested': 'Actuellement pas intéressé par l\'achat / la vente'
      }
    };
    
    // Return translated text or default to English
    return translations[currentLanguage]?.[key] || translations['en'][key] || key;
  }

  render() {
    const { errors, isSubmitting, transition_goals, selectedCard, flippedCards } = this.state;
    const loading = this.props.loading;
    
    // Get business type from user's company or global settings
    const businessType = this.props.user?.company?.business_type || 
                        window.__WIZARD_DATA__?.companySettings?.business_type;
    
    // Filter out the exploring card if business type is p_and_c
    const displayCards = businessType === 'p_and_c' 
      ? this.motivationCards.filter(card => card.id !== 'exploring')
      : this.motivationCards;
      
    // Determine which bucket/card is currently selected
    let selectedBucketId = null;
    if (transition_goals.length > 0) {
      this.motivationCards.forEach(card => {
        if (card.options.some(option => transition_goals.includes(option.id))) {
          selectedBucketId = card.id;
        }
      });
    }
    
    return (
      <div className="max-w-5xl mx-auto bg-succession-bg-primary rounded-lg p-8 shadow-succession-md" style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}>
        {errors.general && (
          <div className="mb-6 p-4 bg-succession-error-bg border border-red-200 rounded-lg text-sm text-succession-error">
            <div className="flex">
              <svg className="w-5 h-5 text-succession-error mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <span>{errors.general}</span>
            </div>
          </div>
        )}
        
        <h2 className="text-2xl font-semibold text-succession-text-primary mb-2 text-center">{this.getTranslatedText('title')}</h2>
        <p className="text-succession-text-secondary max-w-2xl mx-auto text-center mb-6">
          {this.getTranslatedText('subtitle')}
        </p>
        
        {businessType === 'p_and_c' && (
          <div className="mb-6 bg-succession-primary-10 p-4 rounded-lg border border-succession-primary">
            <div className="flex">
              <svg className="w-5 h-5 text-succession-primary mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-succession-primary font-medium">
                {this.getTranslatedText('business.type.message')}
              </p>
            </div>
          </div>
        )}
        
        {transition_goals.length === 0 && errors.transition_goals && (
          <div className="mb-6 p-4 bg-succession-error-bg border border-red-200 rounded-lg text-sm text-succession-error">
            <div className="flex">
              <svg className="w-5 h-5 text-succession-error mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>{this.getTranslatedText('at.least.one')}</span>
            </div>
          </div>
        )}
        
        <div className={`grid grid-cols-1 ${displayCards.length === 2 ? 'lg:grid-cols-2 gap-8' : 'lg:grid-cols-3 gap-6'} mb-10`}>
          {displayCards.map(card => {
            const isCardFlipped = flippedCards[card.id];
            const isCardSelected = this.isCardSelected(card.id);
            const isBucketSelected = selectedBucketId === card.id;
            const isOtherBucketSelected = selectedBucketId && selectedBucketId !== card.id;
            
            return (
              <div 
                key={card.id}
                className={`group cursor-pointer transition-all duration-300 perspective-1000 h-96 
                  ${isOtherBucketSelected ? 'opacity-60 hover:opacity-100' : 'opacity-100'}
                  ${isCardSelected ? 'ring-2 ring-offset-2 ring-succession-primary' : ''}
                `}
                onClick={() => this.handleCardSelect(card.id)}
              >
                <div 
                  className={`relative w-full h-full transition-transform duration-500 preserve-3d shadow-succession-lg rounded-xl
                    ${isCardFlipped ? 'rotate-y-180' : 'rotate-y-0'}
                  `}
                >
                  {/* Card front (main type) */}
                  <div 
                    className={`absolute inset-0 backface-hidden bg-succession-bg-primary border rounded-xl px-6 py-8 flex flex-col items-center justify-center text-center
                      ${isOtherBucketSelected ? 'border-succession-border' : isCardSelected ? 'border-succession-primary' : 'border-succession-border hover:border-succession-primary'}
                      transition-all duration-200 ease-in-out
                    `}
                    style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}
                  >
                    {card.icon}
                    
                    <h3 className="text-xl font-bold mb-3 text-succession-primary" style={{ color: 'var(--succession-primary, #5489B6)' }}>
                      {card.title}
                    </h3>
                    
                    <p className="text-succession-text-secondary mb-6">
                      {card.description}
                    </p>
                    
                    {isBucketSelected && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-succession-success-bg text-succession-success">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {this.getTranslatedText('selected')}
                      </span>
                    )}
                    
                    {isOtherBucketSelected ? (
                      <span className="mt-4 inline-flex items-center text-sm text-succession-text-muted">
                        {this.getTranslatedText('click.to.change')}
                      </span>
                    ) : (
                      <span className="absolute bottom-4 text-sm font-medium text-succession-primary flex items-center">
                        {this.getTranslatedText('click.to.see.options')}
                        <svg className="ml-1 w-4 h-4 transition-transform group-hover:translate-y-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    )}
                  </div>
                  
                  {/* Card back (options) */}
                  <div 
                    className="absolute inset-0 backface-hidden bg-succession-bg-primary border border-indigo-200 rounded-xl p-6 rotate-y-180 flex flex-col"
                    style={{ backgroundColor: 'var(--succession-bg-primary, #ffffff)' }}
                  >
                    <div className="flex justify-between items-center border-b border-succession-border pb-4 mb-4">
                      <h3 className="text-lg font-bold text-succession-primary" style={{ color: 'var(--succession-primary, #5489B6)' }}>
                        {card.title}
                      </h3>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          this.handleCardSelect(card.id);
                        }}
                        className="text-succession-text-muted hover:text-succession-text-primary transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto pr-1 space-y-3">
                      {card.options.map(option => {
                        const isSelected = transition_goals.includes(option.id);
                        return (
                          <div 
                            key={option.id}
                            className={`p-3 border rounded-lg flex items-center transition-all duration-200 
                              ${isSelected 
                                ? 'bg-succession-primary-10 border-succession-primary shadow-sm transform -translate-y-0.5' 
                                : 'border-succession-border hover:border-succession-primary hover:bg-succession-primary-10 hover:-translate-y-0.5'
                              }
                            `}
                            onClick={(e) => {
                              e.stopPropagation();
                              this.toggleGoal(option.id);
                            }}
                          >
                            <div className={`flex-shrink-0 w-5 h-5 mr-3 rounded border transition-colors 
                              ${isSelected 
                                ? 'bg-succession-primary border-succession-primary flex items-center justify-center' 
                                : 'border-succession-border'
                              }
                            `}>
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <span className="text-succession-text-primary font-medium">{option.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mb-6 p-4 bg-succession-primary-10 border border-succession-primary rounded-lg shadow-sm">
          <div className="flex space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-6 w-6 text-succession-primary" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-succession-primary mb-1">{this.getTranslatedText('selection.rule.title')}</p>
              <p className="text-succession-primary text-sm">{this.getTranslatedText('selection.rule')}</p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end pt-6 border-t border-succession-border">
          <button 
            onClick={this.handleSubmit}
            className={`inline-flex justify-center items-center py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white
              ${isSubmitting || loading ? 'bg-succession-primary opacity-60 cursor-not-allowed' : 'bg-succession-primary hover:bg-succession-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-succession-primary'} 
              transition-colors
            `}
            disabled={isSubmitting || loading}
          >
            {isSubmitting || loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {this.getTranslatedText('saving')}
              </>
            ) : (
              <>
                {this.getTranslatedText('continue')}
                <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </>
            )}
          </button>
        </div>

        <style jsx>{`
          .perspective-1000 {
            perspective: 1000px;
          }
          .preserve-3d {
            transform-style: preserve-3d;
          }
          .backface-hidden {
            backface-visibility: hidden;
          }
          .rotate-y-0 {
            transform: rotateY(0deg);
          }
          .rotate-y-180 {
            transform: rotateY(180deg);
          }
        `}</style>
      </div>
    );
  }
}

MotivationStep.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
    transition_goals: PropTypes.arrayOf(PropTypes.string),
    company: PropTypes.shape({
      business_type: PropTypes.string
    })
  }),
  onNext: PropTypes.func,
  onUserUpdate: PropTypes.func,
  mutate: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onBack: PropTypes.func,
  currentLanguage: PropTypes.string,
  onboardingContext: PropTypes.object
};

// Wrap component with the GraphQL mutation
export default graphql(UPDATE_CURRENT_USER, {
  options: { 
    errorPolicy: 'all' 
  }
})(withOnboardingContext(MotivationStep)); 